import React, { useMemo, useState } from 'react';
import DialogFrame from '../_parts/DialogFrame';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { showFileSelectDialog } from '../../../../redux/project/file/sliceProjectFileOpen';
import {
    Checkbox,
    DialogActions,
    DialogContent,
    Divider,
    FormControlLabel,
    Stack,
    Typography,
} from '@mui/material';
import CtaButton from '../_parts/CtaButton';
import { IAppReduxStore } from '../../../../redux/store';
import CheckboxTree from '../_parts/CheckboxTree';
import LabelAndCaption from '../_parts/LabelAndCaption';
import { getFileTypeOrDefault } from '../../../../util/FileUtils';
import { ContentLayout } from '../_parts/ContentLayout';
import { docTableActionsCallbacks as actionCallbacks } from '../../DocTable/DocTableActions';
import { useParams } from 'react-router';

export default () => {
    const { replicaId, areaName } = useParams();
    const { t } = useTranslation('translations', { keyPrefix: 'fileSelectionDialog' });
    const dispatch = useDispatch();

    const { universalId } = useSelector(
        (store: IAppReduxStore) => store.projectFileOpen.fileSelectDialog,
    );
    const { data, loading } = useSelector((store: IAppReduxStore) => store.projectDocumentsFiles);
    const [selectedFiles, setSelectedFiles] = useState<string[]>([]);

    const fileOptions = useMemo(
        () =>
            (data?.[universalId] ?? []).map(file => {
                const fileType = getFileTypeOrDefault(file.fileName);
                return {
                    id: file.fileIndex.toString(),
                    path: file.fileIndex.toString(),
                    label: file.fileName,
                    parent: null,
                    icon: {
                        childIcon: {
                            path: fileType.icon,
                            color: fileType.color,
                        },
                    },
                };
            }),
        [data],
    );

    const onClose = () => {
        dispatch(showFileSelectDialog(null));
    };

    const onConfirm = () => {
        dispatch(showFileSelectDialog(null));
        selectedFiles.forEach(fileId => {
            const { fileName, fileIndex } = data[universalId].find(
                x => x.fileIndex.toString() === fileId,
            );
            actionCallbacks.view({
                replicaId,
                areaName,
                universalId,
                fileName,
                fieldIndex: fileIndex.toString(),
                fieldName: 'File',
            });
        });
    };

    const onSelectionChanged = (id: string, isChecked: boolean) => {
        setSelectedFiles(prevState => prevState.filter(x => x !== id).concat(isChecked ? id : []));
    };

    const onCheckboxChanged = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setSelectedFiles(checked ? fileOptions.map(x => x.id) : []);
    };

    return (
        <DialogFrame open>
            <DialogHeader dialogTitle={t('title')} onClose={onClose} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={loading}
                    main={
                        <>
                            <LabelAndCaption label={t('label')} caption=''></LabelAndCaption>
                            <Stack
                                direction='row'
                                sx={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography>
                                    {t('selectedCount', { count: selectedFiles.length })}
                                </Typography>
                                <FormControlLabel
                                    labelPlacement='start'
                                    control={
                                        <Checkbox
                                            checked={selectedFiles.length === fileOptions.length}
                                            onChange={onCheckboxChanged}
                                        />
                                    }
                                    label={<Typography>{t('selectAll')}</Typography>}
                                />
                            </Stack>
                            <CheckboxTree
                                withSearch
                                items={fileOptions}
                                checkedNodes={selectedFiles}
                                onCheckNode={onSelectionChanged}
                            />
                        </>
                    }
                />
            </DialogContent>
            <Divider />
            <DialogActions sx={{ paddingX: '24px' }}>
                <CtaButton text={t('cancel')} variant='outlined' onClick={onClose} />
                <CtaButton
                    disabled={!selectedFiles.length}
                    text={t('confirm')}
                    variant='contained'
                    onClick={onConfirm}
                />
            </DialogActions>
        </DialogFrame>
    );
};

export interface IFileSelectionDialogConfig {
    universalId: string;
}
